import React from 'react';
import { Link } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';
import SEO, { SITE_URL } from './SEO';
import Footer from './Footer';
import LiveDemoStrip from './LiveDemoStrip';
import './PricingPage.css';

const TIERS = [
  {
    name: 'Starter',
    priceLine: 'From $500/month · 500 minutes included',
    blurb:
      'For single-location businesses that want every call answered, every message captured, and simple bookings handled — without hiring anyone.',
    bullets: [
      '24/7 answering — first ring, every call, nights and holidays included',
      'FAQ handling: hours, location, services, pricing ranges',
      'Message taking with name, number, and what the caller wanted',
      'Simple appointment booking into your calendar',
      'Call summaries texted or emailed to you after every call',
    ],
    cta: 'Start with a free audit',
  },
  {
    name: 'Growth',
    priceLine: 'From $899/month · 1,500 minutes included',
    blurb:
      'For businesses where the phone drives revenue: integrations, bilingual coverage, lead qualification, and clean handoffs to your team.',
    bullets: [
      'Everything in Starter',
      'CRM and scheduling integrations — notes and bookings land in your tools',
      'Bilingual English/Spanish call flows',
      'Lead qualification with your screening questions',
      'Warm transfer and escalation rules for calls that need a human',
      'After-hours and overflow workflows tuned to your business',
    ],
    cta: 'Scope it with a free audit',
    featured: true,
  },
  {
    name: 'Custom',
    priceLine: 'From $1,799/month · scoped after your audit',
    blurb:
      'For multi-location operations and complex call flows — legal intake, medical scheduling, multiple departments, custom reporting.',
    bullets: [
      'Multi-location and multi-number routing',
      'Industry-specific intake workflows (legal, medical, home services)',
      'Custom integrations beyond standard CRM and calendar connections',
      'Reporting on call volume, outcomes, and recovered leads',
      'Ongoing tuning based on real call recordings',
    ],
    cta: 'Talk it through in a free audit',
  },
];

const COMPARISON = [
  ['Item', 'AI receptionist (UBOTIKA)', 'Human receptionist', 'Answering service'],
  ['Typical monthly cost', 'From $500/month flat, with minutes included', '$3,000–$4,500 fully loaded (wages, taxes, benefits)', 'Metered from the first minute, often $1–$2/min — climbs with volume'],
  ['Hours covered', '24/7/365, holidays included, at the same flat price', 'One shift, minus breaks, sick days, and vacation', '24/7 possible, but after-hours is often a paid add-on'],
  ['Answer speed', 'First ring, every time', 'Fast at the desk; rolls to voicemail when busy or away', 'Queues and hold time during busy stretches'],
  ['Simultaneous calls', 'Unlimited — ten at once handled like one', 'One at a time', 'Limited by staffing; overflow callers wait'],
  ['Books appointments', 'Yes — checks availability, books, and confirms on the same call', 'Yes', 'Usually no — takes a message you still have to return'],
  ['CRM and calendar updates', 'Automatic notes and bookings on every call', 'Manual, when there’s time', 'Rare — typically an emailed message'],
  ['Bilingual English/Spanish', 'Built into the call flow', 'Only if you hire bilingual staff for every shift', 'Premium add-on, subject to operator availability'],
  ['Best suited for', 'The routine majority: answering, qualifying, booking', 'Complex, emotional, high-touch conversations', 'Basic message-taking and overflow'],
];

export const FAQS = [
  {
    q: 'What does an AI receptionist cost with UBOTIKA?',
    a: 'Plans start at $500 a month for 24/7 call coverage with 500 minutes included. Add appointment booking, lead qualification, bilingual call flows, and a CRM or calendar integration and you’re at $899. Full intake across phone, email, and web chat is $1,799. Multi-location and complex operations are scoped individually. For comparison, a human receptionist runs $3,000–$4,500 a month fully loaded.',
  },
  {
    q: 'Is it a flat fee, or do I pay per minute?',
    a: 'Flat. An answering service meters you from the very first minute, so the busier you get the more you’re punished for it. Your UBOTIKA price is a fixed monthly number with a generous minute allowance built in. If you consistently run past your allowance we move you to the right plan rather than surprise you on an invoice — and any overage rate is published and agreed before you sign, never discovered afterward.',
  },
  {
    q: 'Are there setup fees or hidden costs?',
    a: 'There’s a one-time setup fee — from $750, depending on the plan — that covers call-flow design, integrations, and testing before you go live. We waive it entirely on a 12-month agreement. Beyond that, your quote spells out the monthly price and everything included in one document, before you sign.',
  },
  {
    q: 'How do I know which tier I need?',
    a: 'You don’t have to guess — that’s what the audit is for. We look at your call volume, where calls slip through, what tools you use, and whether you need bilingual or after-hours coverage, then recommend the smallest setup that solves the problem. If an AI receptionist doesn’t make financial sense for your business, we’ll tell you that too.',
  },
  {
    q: 'Can I hear it before I pay anything?',
    a: 'Yes — call (424) 434-4541 right now. An AI answers that line, because the demo is the product. Ask it questions the way your customers would. If it doesn’t impress you, you’ve lost two minutes.',
  },
  {
    q: 'Can I try it on my own calls before committing?',
    a: 'Yes. Try your AI receptionist on your own calls free for two weeks. We build your agent, point your calls at it, and you listen to recordings of your own business being answered. If you continue after the free trial, the Starter plan is $500 per month. The $500 is the monthly service price, not a charge for the free trial. Higher-tier plans and any setup fees are confirmed in your quote.',
  },
];

const schema = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'AI Receptionist Pricing — UBOTIKA',
    description:
      'UBOTIKA AI receptionist pricing: flat monthly plans from $500/month with minutes included, scoped after a free audit — a fraction of a $3,000+/month human receptionist.',
    provider: {
      '@type': 'LocalBusiness',
      '@id': `${SITE_URL}/#organization`,
      name: 'UBOTIKA',
      url: SITE_URL,
      telephone: '+1-310-912-3548',
      email: 'hello@ubotika.com',
    },
    areaServed: { '@type': 'City', name: 'Los Angeles' },
    offers: { '@type': 'Offer', availability: 'https://schema.org/InStock', priceCurrency: 'USD' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Pricing', item: `${SITE_URL}/pricing` },
    ],
  },
];

function PricingPage() {
  return (
    <>
      <SEO
        title="AI Receptionist Pricing | Plans From $500/mo | UBOTIKA"
        description="AI receptionist plans from $500/month with minutes included. Try it free for two weeks on your own calls. Free audit, clear monthly pricing, no per-call meter."
        path="/pricing"
        image={`${SITE_URL}/og/pricing.png`}
        schema={schema}
      />
      <main id="main-content" className="pricing-page">
        <section className="pricing-hero">
          <h1>One flat monthly price. Minutes included. No surprises.</h1>
          <p className="pricing-sub">
            Plans start at $500 a month with minutes included. Where you land depends on your
            call volume, integrations, and coverage — which is exactly what the free audit
            settles, before you commit. Most setups land far below the cost of a single
            part-time hire.
          </p>
        </section>

        <section className="pricing-tiers">
          {TIERS.map((tier) => (
            <article className={`pricing-tier ${tier.featured ? 'featured' : ''}`} key={tier.name}>
              <h2>{tier.name}</h2>
              <p className="pricing-tier-price">{tier.priceLine}</p>
              <p className="pricing-tier-blurb">{tier.blurb}</p>
              <ul>
                {tier.bullets.map((b) => (
                  <li key={b}>
                    <FaCheck className="pricing-check" /> {b}
                  </li>
                ))}
              </ul>
              <Link to="/free-missed-lead-audit?src=pricing" className="cta-button pricing-tier-cta">
                {tier.cta}
              </Link>
            </article>
          ))}
        </section>

        <section className="pricing-compare">
          <h2>How the math compares</h2>
          <div className="pricing-table-wrap">
            <table>
              <thead>
                <tr>
                  {COMPARISON[0].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON.slice(1).map((row) => (
                  <tr key={row[0]}>
                    {row.map((cell, i) => (
                      <td key={`${row[0]}-${i}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="pricing-faq">
          <h2>Pricing questions, answered straight</h2>
          <div className="pricing-faq-list">
            {FAQS.map((f) => (
              <article className="pricing-faq-item" key={f.q}>
                <h3>{f.q}</h3>
                <p>{f.a}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="pricing-final">
          <h2>Find out what missed calls cost you — and what fixing them would cost</h2>
          <p>
            The free audit shows how many calls your business is likely missing, what they&apos;re
            worth, and one fixed monthly price to fix it. No obligation, and a straight answer
            either way. Prefer to hear it first? Call (424) 434-4541 — an AI picks up.
          </p>
          <Link to="/free-missed-lead-audit?src=pricing" className="cta-button">
            Get my free audit
          </Link>
        </section>

        <LiveDemoStrip location="pricing" />
        <Footer />
      </main>
    </>
  );
}

export default PricingPage;
